import { useState } from "react";
import { createOffer, defaultExpiry, type OffersDto } from "../lib/offersPage";
import { Field, Select } from "./Form";
export function AddOffer({
  data,
  onAdded,
  onError,
}: {
  data: OffersDto;
  onAdded: () => void;
  onError: (m: string) => void;
}) {
  const [cardId, setCardId] = useState(String(data.cards[0]?.id ?? ""));
  const [merchant, setMerchant] = useState("");
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [threshold, setThreshold] = useState("");
  const [type, setType] = useState(data.cashback_types[0] ?? "dollars");
  const [expires, setExpires] = useState(defaultExpiry(data.today));
  const [notes, setNotes] = useState("");
  // Checked by default, matching the live form. The common gesture is adding
  // an offer you have just enrolled in on the issuer's site.
  const [enrolled, setEnrolled] = useState(true);
  const [busy, setBusy] = useState(false);

  return (
    <form
      aria-label="Add offer"
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        createOffer({
          card_id: Number(cardId),
          merchant,
          cashback_type: type,
          // Empty stays undefined rather than becoming 0: zero means "not
          // specified" server-side, and sending 0 would be indistinguishable
          // from a deliberate zero-value offer.
          cashback_amount: amount === "" ? undefined : Number(amount),
          spend_threshold: threshold === "" ? undefined : Number(threshold),
          expires_on: expires || undefined,
          title,
          notes,
          enrolled,
        })
          .then(() => {
            setMerchant("");
            setTitle("");
            setAmount("");
            setThreshold("");
            setNotes("");
            setEnrolled(true);
            setExpires(defaultExpiry(data.today));
            onAdded();
          })
          .catch((err: unknown) =>
            onError(err instanceof Error ? err.message : String(err)),
          )
          .finally(() => setBusy(false));
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Card
          <select
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
            className="min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
          >
            {data.cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <Field label="Merchant" value={merchant} onChange={setMerchant} />
        {/* The offer's actual terms. Without it the list showed the merchant
            and the reward and never what you have to do to earn it. */}
        <Field label="Description" value={title} onChange={setTitle} />
        <Select
          label="Reward type"
          value={type}
          options={data.cashback_types}
          onChange={setType}
        />
        <Field label="Reward amount" value={amount} onChange={setAmount} type="number" />
        <Field
          label="Spend required"
          value={threshold}
          onChange={setThreshold}
          type="number"
        />
        <Field label="Expires on" value={expires} onChange={setExpires} type="date" />
        <Field label="Notes" value={notes} onChange={setNotes} />
      </div>
      <label className="flex items-center gap-2 text-xs text-neutral-400">
        <input
          type="checkbox"
          className="h-4 w-4 accent-sky-400"
          checked={enrolled}
          onChange={(e) => setEnrolled(e.target.checked)}
        />
        Already added to card
      </label>
      <button
        type="submit"
        disabled={busy}
        className="btn btn-primary"
      >
        {busy ? "Adding…" : "Add offer"}
      </button>
    </form>
  );
}
